import { useEffect, useRef, useState } from 'react';
import { isAxiosError } from 'axios';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    Alert, Button, Card, Checkbox, Col, DatePicker, Descriptions, Form, Input, InputNumber,
    Radio, Row, Select, Space, Steps, Typography,
} from 'antd';
import dayjs from 'dayjs';
import API from 'app/utils/api/axios';
import PageWithLoader from 'app/hoc/page_with_loader/page_with_loader';
import API_URL from 'app/constants/api_urls';
import URL from 'app/constants/route_urls';
import { Prompt } from 'app/hooks';
import Signature from './signature';
import './event_insurance.scss';

type Value = string | number | undefined;
type Answers = Record<string, Value>;
type EventKind = 'group' | 'virtual';
type InsuranceEvent = Answers & { kind: EventKind };
type Field = {
    key: string;
    label: string;
    kind: 'text' | 'radio' | 'select' | 'date' | 'integer' | 'money';
    choices: string[] | null;
    when: Record<string, string> | null;
    help: string | null;
};
type Program = {
    max_event_date: string;
    events: Record<EventKind, Field[]>;
    organization: Field[];
    coverage: Field[];
    declaration: string;
    applicant_name: string;
};
type Quote = {
    pk: number;
    events: InsuranceEvent[];
    answers: Answers;
    date_signed: string | null;
    date_updated: string;
    applicant_name: string;
};

const STEPS = ['Program details', 'Events', 'Organization & coverage', 'Review & sign'];
const QUERY_KEY = ['insurance-quotes'];
const isVisible = (field: Field, answers: Answers) =>
    Object.entries(field.when ?? {}).every(([key, value]) => answers[key] === value);

type FieldErrors = Record<string, string>;

const requiredErrors = (fields: Field[], values: Answers, prefix: string): FieldErrors => Object.fromEntries(
    fields.filter((field) => isVisible(field, values) && !String(values[field.key] ?? '').trim())
        .map((field) => [`${prefix}-${field.key}`, 'This field is required.']),
);

const applicationName = (events: InsuranceEvent[]) => `Application - ${String(events[0]?.name ?? '').trim() || 'Untitled draft'}`;
const eventName = (event: InsuranceEvent, index: number) =>
    String(event.name ?? '').trim() || `${event.kind === 'group' ? 'Group' : 'Virtual'} event ${index + 1}`;

const validationErrors = (error: unknown): FieldErrors => {
    if (!isAxiosError(error) || error.response?.status !== 400) return {};
    const errors: FieldErrors = {};
    const add = (id: string, value: unknown) => {
        const message = Array.isArray(value) ? value.find((item) => typeof item === 'string') : value;
        if (typeof message === 'string') errors[id] = message;
    };
    const readFields = (prefix: string, value: unknown) => {
        if (value && typeof value === 'object') {
            Object.entries(value).forEach(([key, message]) => add(`${prefix}-${key}`, message));
        }
    };
    const data = error.response.data;
    if (!data || typeof data !== 'object') return errors;
    readFields('answer', data.answers);
    if (data.events && typeof data.events === 'object' && !Array.isArray(data.events)) {
        Object.entries(data.events).forEach(([index, event]) => readFields(`event-${index}`, event));
    } else {
        add('insurance-add-event', data.events);
    }
    add('insurance-name', data.applicant_name);
    add('insurance-birthdate', data.applicant_birthdate);
    add('insurance-signature', data.declaration_signature);
    return errors;
};

const setAnswer = (fields: Field[], answers: Answers, key: string, value: Value) => {
    const updated = { ...answers, [key]: value };
    fields.forEach((field) => {
        if (!isVisible(field, updated)) delete updated[field.key];
    });
    return updated;
};

const FieldControl = ({
    field, values, maxDate, onChange, inputId, error,
}: { field: Field; values: Answers; maxDate: string; onChange: (value: Value) => void; inputId: string; error?: string }) => {
    const value = values[field.key];
    const id = inputId;
    let control;
    if (field.kind === 'radio') {
        control = <Radio.Group id={id} aria-label={field.label} options={field.choices ?? []}
            value={value} onChange={(event) => onChange(event.target.value)} />;
    } else if (field.kind === 'select') {
        control = <Select id={id} showSearch optionFilterProp='label' placeholder='Select an option'
            options={field.choices?.map((choice) => ({ label: choice, value: choice }))}
            value={value} onChange={onChange} />;
    } else if (field.kind === 'date') {
        control = <DatePicker id={id} style={{ width: '100%' }}
            value={value ? dayjs(value) : null}
            maxDate={dayjs(maxDate)}
            disabledDate={(date) => date.isAfter(dayjs(maxDate), 'day') ||
                (field.key === 'end_date' && !!values.start_date && date.isBefore(dayjs(values.start_date), 'day'))}
            onChange={(date) => onChange(date?.format('YYYY-MM-DD'))} />;
    } else if (field.kind === 'integer' || field.kind === 'money') {
        control = <InputNumber id={id} style={{ width: '100%' }}
            value={typeof value === 'number' ? value : null}
            min={field.kind === 'money' ? 0 : 1} max={2147483647} precision={0}
            onChange={(number) => onChange(number ?? undefined)} />;
    } else {
        control = <Input id={id} maxLength={500} value={value}
            onChange={(event) => onChange(event.target.value)} />;
    }
    return <Form.Item label={field.label} htmlFor={id} required extra={field.help}
        data-insurance-field={id} validateStatus={error ? 'error' : undefined} help={error}>{control}</Form.Item>;
};

const ProgramDetails = () => (
    <Space direction='vertical' size='middle' style={{ width: '100%' }}>
        <Descriptions column={1} bordered size='small' items={[
            { key: 'limits', label: 'Liability limits', children: 'Standard: $1M per occurrence / $2M aggregate. Optional: $2M per occurrence / $4M aggregate for an additional premium.' },
            { key: 'changes', label: 'Policy amendments', children: 'Active policies cannot be amended to add or cancel events, change dates or add optional coverage. New events require a separate policy.' },
            { key: 'cancellations', label: 'Cancellation', children: 'Cancellation is permitted only before coverage starts. Fees are non-refundable.' },
        ]} />
        <Alert type='info' showIcon message='Coverage exclusions' description={
            <>
                <p>The following types of events are excluded:</p>
                <ul className='insurance-wizard__exclusions'>
                    <li>Triathlons and duathlons</li>
                    <li>Multisport events and adventure races</li>
                    <li>Cycling events</li>
                    <li>Swimming, paddling and watercraft events</li>
                    <li>Events with animals other than documented service animals</li>
                    <li>Motorsports and shooting events</li>
                    <li>Personal or household events</li>
                </ul>
                <Typography.Title level={5}>Other exclusions</Typography.Title>
                <ul className='insurance-wizard__exclusions'>
                    <li>Employee and volunteer workers, whether paid or unpaid</li>
                    <li>Medical personnel, including volunteers, EMTs and athletic trainers, and their professional acts</li>
                    <li>Fireworks</li>
                    <li>Rappelling</li>
                    <li>Trampolines</li>
                    <li>Zip lines</li>
                    <li>Aviation</li>
                </ul>
            </>
        } />
    </Space>
);

const QuoteWizard = ({ program, quotes }: { program: Program; quotes: Quote[] }) => {
    const queryClient = useQueryClient();
    const [quote, setQuote] = useState<Quote | undefined>(quotes[0]);
    const [events, setEvents] = useState<InsuranceEvent[]>(quotes[0]?.events ?? []);
    const [answers, setAnswers] = useState<Answers>(quotes[0]?.answers ?? {});
    const [step, setStep] = useState(0);
    const [error, setError] = useState('');
    const [signature, setSignature] = useState('');
    const [birthdate, setBirthdate] = useState('');
    const [accepted, setAccepted] = useState(false);
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
    const [baseline, setBaseline] = useState(() => JSON.stringify({ events, answers }));
    const [failedSnapshot, setFailedSnapshot] = useState('');
    const scrollPending = useRef(false);
    const pending = useRef(false);
    const snapshot = JSON.stringify({ events, answers });
    const dirty = snapshot !== baseline;
    const fields = [...program.organization, ...program.coverage].filter((field) => field.key !== 'membership');

    const save = useMutation({
        mutationFn: async ({ pk, body }: { pk?: number; body: object }) => {
            const { data } = pk
                ? await API.patch<Quote>(`${API_URL.INSURANCE_QUOTES}${pk}/`, body)
                : await API.post<Quote>(API_URL.INSURANCE_QUOTES, body);
            return data;
        },
        onSuccess: (data) => {
            setQuote(data);
            if (data.date_signed) {
                setBirthdate('');
                setSignature('');
                setAccepted(false);
            }
            queryClient.setQueryData<Quote[]>(QUERY_KEY, (existing) =>
                [data, ...(existing ?? []).filter((item) => item.pk !== data.pk)]);
        },
    });
    const { mutateAsync } = save;
    const signing = save.isPending && !!save.variables?.body && 'declaration_signature' in save.variables.body;

    useEffect(() => {
        if (!dirty || quote?.date_signed || save.isPending || snapshot === failedSnapshot) return undefined;
        const timer = window.setTimeout(async () => {
            if (pending.current) return;
            pending.current = true;
            try {
                await mutateAsync({ pk: quote?.pk, body: JSON.parse(snapshot) });
                setBaseline(snapshot);
                setError('');
            } catch (failure) {
                setFailedSnapshot(snapshot);
                const issues = validationErrors(failure);
                if (Object.keys(issues).length) setFieldErrors((previous) => ({ ...previous, ...issues }));
                setError(Object.keys(issues).length
                    ? 'Some changes couldn’t be saved. Check the highlighted fields.'
                    : 'We couldn’t autosave your changes. Edit a field or retry to save them.');
            } finally {
                pending.current = false;
            }
        }, 450);
        return () => window.clearTimeout(timer);
    }, [dirty, failedSnapshot, mutateAsync, quote?.date_signed, quote?.pk, save.isPending, snapshot]);

    useEffect(() => {
        if (!scrollPending.current) return;
        scrollPending.current = false;
        const element = [...document.querySelectorAll<HTMLElement>('[data-insurance-field]')]
            .find((item) => fieldErrors[item.dataset.insuranceField ?? '']);
        if (!element) return;
        const navHeight = document.querySelector<HTMLElement>('.top-navbar__header')?.offsetHeight ?? 0;
        window.scrollTo({ top: element.getBoundingClientRect().top + window.scrollY - navHeight - 30, behavior: 'smooth' });
    }, [fieldErrors, step]);

    const reset = (selected?: Quote) => {
        const nextEvents = selected?.events ?? [];
        const nextAnswers = selected?.answers ?? {};
        setQuote(selected);
        setEvents(nextEvents);
        setAnswers(nextAnswers);
        setBaseline(JSON.stringify({ events: nextEvents, answers: nextAnswers }));
        setFailedSnapshot('');
        setStep(0);
        setError('');
        setFieldErrors({});
        setSignature('');
        setBirthdate('');
        setAccepted(false);
    };

    const report = (issues: FieldErrors, stage: number) => {
        scrollPending.current = true;
        setFieldErrors(issues);
        setStep(stage);
    };

    const submit = async () => {
        setError('');
        let issues: FieldErrors = {};
        if (step === 1) {
            if (!events.length) issues['insurance-add-event'] = 'Add at least one event.';
            events.forEach((event, index) => {
                const prefix = `event-${index}`;
                Object.assign(issues, requiredErrors(program.events[event.kind], event, prefix));
                (['start_date', 'end_date'] as const).forEach((key) => {
                    if (event[key] && String(event[key]) > program.max_event_date) {
                        issues[`${prefix}-${key}`] = 'This date is outside the program period.';
                    }
                });
                if (!issues[`${prefix}-end_date`] && event.start_date && event.end_date &&
                    String(event.start_date) > String(event.end_date)) {
                    issues[`${prefix}-end_date`] = 'The end date cannot precede the start date.';
                }
            });
        }
        if (step === 2) issues = requiredErrors(fields, answers, 'answer');
        if (step === 3) {
            if (!program.applicant_name) issues['insurance-name'] = 'Add your name to your account profile.';
            if (!birthdate) issues['insurance-birthdate'] = 'Enter your date of birth.';
            else if (dayjs(birthdate).isAfter(dayjs().subtract(18, 'year'), 'day') ||
                dayjs(birthdate).isBefore(dayjs().subtract(131, 'year').add(1, 'day'), 'day')) {
                issues['insurance-birthdate'] = 'Enter a valid date of birth. The applicant must be at least 18.';
            }
            if (!signature) issues['insurance-signature'] = 'Draw your signature.';
            if (!accepted) issues['insurance-accepted'] = 'Accept the applicant declaration.';
        }
        if (Object.keys(issues).length) {
            report(issues, step);
            return;
        }
        if (step < 3) {
            setFieldErrors({});
            setStep(step + 1);
            return;
        }
        if (pending.current) return;
        pending.current = true;
        try {
            await mutateAsync({
                pk: quote?.pk,
                body: { events, answers, applicant_birthdate: birthdate,
                    declaration_signature: signature, declaration_accepted: accepted },
            });
            setBaseline(snapshot);
        } catch (failure) {
            const issues = validationErrors(failure);
            const ids = Object.keys(issues);
            if (ids.length) {
                const target = ids.some((id) => id.startsWith('event-') || id === 'insurance-add-event')
                    ? 1 : ids.some((id) => id.startsWith('answer-')) ? 2 : 3;
                report(issues, target);
            } else {
                setError('We couldn’t save your signed application. Please try again.');
            }
        } finally {
            pending.current = false;
        }
    };

    const clearError = (id: string) => setFieldErrors((previous) => {
        if (!previous[id]) return previous;
        const next = { ...previous };
        delete next[id];
        return next;
    });
    const feedback = (id: string) => ({
        'data-insurance-field': id,
        validateStatus: fieldErrors[id] ? 'error' as const : undefined,
        help: fieldErrors[id],
    });

    const renderFields = (fields: Field[], values: Answers, update: (values: Answers) => void, prefix: string) => (
        <Row gutter={24}>
            {fields.filter((field) => isVisible(field, values)).map((field) => (
                <Col xs={24} md={field.kind === 'radio' ? 24 : 12} key={field.key}>
                    <FieldControl field={field} values={values} maxDate={program.max_event_date}
                        onChange={(value) => {
                            clearError(`${prefix}-${field.key}`);
                            update(setAnswer(fields, values, field.key, value));
                        }}
                        error={fieldErrors[`${prefix}-${field.key}`]}
                        inputId={`${prefix}-${field.key}`} />
                </Col>
            ))}
        </Row>
    );

    const review = (fields: Field[], values: Answers) => (
        <Descriptions column={1} size='small' bordered items={fields
            .filter((field) => isVisible(field, values))
            .map((field) => ({ key: field.key, label: field.label, children: values[field.key] ?? '—' }))} />
    );

    const reviewEvents = () => events.map((event, index) => (
        <Card size='small' key={index} title={eventName(event, index)}>
            {review(program.events[event.kind], event)}
        </Card>
    ));

    return (
        <Space className='insurance-wizard' direction='vertical' size='large' style={{ width: '100%' }}>
            <Prompt when={dirty || signing} beforeUnload message='Your insurance changes are still unsaved. Leave this page?' />
            {!!quotes.length && <Space wrap>
                <Typography.Text>Application history</Typography.Text>
                <Select aria-label='Application history' placeholder='Application history'
                    className='insurance-wizard__history' value={quote?.pk} disabled={dirty || save.isPending}
                    options={quotes.map((item) => ({
                        value: item.pk,
                        label: applicationName(item.pk === quote?.pk ? events : item.events),
                        status: item.date_signed ? 'Signed' : 'Draft',
                        updated: dayjs(item.date_updated).format('MMM D, YYYY'),
                    }))}
                    optionRender={({ data }) => <Space direction='vertical' size={0}>
                        <span>{data.label}</span>
                        <Typography.Text type='secondary'>{data.status} · {data.updated}</Typography.Text>
                    </Space>}
                    onChange={(pk) => reset(quotes.find((item) => item.pk === pk))} />
                {!!quote && <Button disabled={dirty || save.isPending} onClick={() => reset()}>Start a new quote</Button>}
            </Space>}
            {quote?.date_signed ? (
                <Card title='Signed application'>
                    <Space direction='vertical' size='middle' style={{ width: '100%' }}>
                        <Alert type='info' showIcon message='This application has not been sent to Nicholas Hill Group or priced.' />
                        <Typography.Title level={4}>Events</Typography.Title>
                        {reviewEvents()}
                        <Typography.Title level={4}>Organization & coverage</Typography.Title>
                        {review(fields, answers)}
                        {quote.applicant_name && <Typography.Text>Applicant: {quote.applicant_name}</Typography.Text>}
                    </Space>
                </Card>
            ) : (
                <>
                    <Steps current={step} size='small' items={STEPS.map((title) => ({ title }))} />
                    <Card className='insurance-wizard__stage' title={STEPS[step]}>
                        <Form layout='vertical' disabled={signing}>
                            {step === 0 && <ProgramDetails />}
                            {step === 1 && <Space direction='vertical' size='middle' style={{ width: '100%' }}>
                                <Typography.Paragraph>Add each in-person or virtual running event you want to insure. Events on one policy share the same limits.</Typography.Paragraph>
                                {events.map((event, index) => (
                                    <Card key={index} size='small' title={eventName(event, index)}
                                        extra={<Button type='link' danger onClick={() => {
                                            setEvents(events.filter((_, i) => i !== index));
                                            setFieldErrors({});
                                        }}>Remove</Button>}>
                                        {renderFields(program.events[event.kind], event, (next) => {
                                            setEvents(events.map((item, i) => i === index ? { ...next, kind: event.kind } : item));
                                        }, `event-${index}`)}
                                    </Card>
                                ))}
                                <Form.Item {...feedback('insurance-add-event')}>
                                    <Space wrap>
                                        <Button disabled={events.length >= 100} onClick={() => {
                                            clearError('insurance-add-event');
                                            setEvents([...events, { kind: 'group' }]);
                                        }}>Add group event</Button>
                                        <Button disabled={events.length >= 100} onClick={() => {
                                            clearError('insurance-add-event');
                                            setEvents([...events, { kind: 'virtual' }]);
                                        }}>Add virtual event</Button>
                                    </Space>
                                </Form.Item>
                            </Space>}
                            {step === 2 && renderFields(fields, answers, setAnswers, 'answer')}
                            {step === 3 && <Space direction='vertical' size='middle' style={{ width: '100%' }}>
                                <Typography.Title level={4}>Events</Typography.Title>
                                {reviewEvents()}
                                <Typography.Title level={4}>Organization & coverage</Typography.Title>
                                {review(fields, answers)}
                                <Typography.Title level={4}>Applicant declaration</Typography.Title>
                                <Typography.Paragraph>{program.declaration}</Typography.Paragraph>
                                <Row gutter={24}>
                                    <Col xs={24} md={12}>
                                        <Form.Item label='Applicant name' htmlFor='insurance-name' required {...feedback('insurance-name')}
                                            extra={<Link to={URL.PROFILE}>Edit your profile name</Link>}>
                                            <Input id='insurance-name' value={program.applicant_name} readOnly />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={8}>
                                        <Form.Item label='Applicant date of birth' htmlFor='insurance-birthdate' required
                                            extra='The applicant must be at least 18 years old.' {...feedback('insurance-birthdate')}>
                                            <DatePicker id='insurance-birthdate' value={birthdate ? dayjs(birthdate) : null}
                                                maxDate={dayjs().subtract(18, 'year')} minDate={dayjs().subtract(131, 'year').add(1, 'day')}
                                                onChange={(date) => {
                                                    clearError('insurance-birthdate');
                                                    setBirthdate(date?.format('YYYY-MM-DD') ?? '');
                                                    setAccepted(false);
                                                }} />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Form.Item label='Applicant signature' required {...feedback('insurance-signature')}>
                                    <Signature value={signature} onChange={(value) => {
                                        clearError('insurance-signature');
                                        setSignature(value);
                                        setAccepted(false);
                                    }} />
                                </Form.Item>
                                <Form.Item {...feedback('insurance-accepted')}>
                                    <Checkbox checked={accepted} onChange={(event) => {
                                        clearError('insurance-accepted');
                                        setAccepted(event.target.checked);
                                    }}>
                                        I have read and agree to the applicant declaration.
                                    </Checkbox>
                                </Form.Item>
                            </Space>}
                        </Form>
                    </Card>
                    {error && <Alert type='error' showIcon message={error} action={dirty && failedSnapshot === snapshot
                        ? <Button size='small' onClick={() => setFailedSnapshot('')}>Retry autosave</Button> : undefined} />}
                    <div className='insurance-wizard__actions'>
                        <div>{step > 0 && <Button onClick={() => {
                            setStep(step - 1); setFieldErrors({}); setSignature(''); setAccepted(false);
                        }} disabled={signing}>Back</Button>}</div>
                        <Button type='primary' loading={step === 3 && save.isPending} onClick={submit}>
                            {step === 3 ? 'Save signed application' : 'Continue'}
                        </Button>
                    </div>
                </>
            )}
        </Space>
    );
};

const EventInsurance = () => {
    const program = useQuery({
        queryKey: ['insurance-program'],
        queryFn: async ({ signal }) => (await API.get<Program>(API_URL.INSURANCE_PROGRAM, { signal })).data,
    });
    const quotes = useQuery({
        queryKey: QUERY_KEY,
        queryFn: async ({ signal }) => (await API.get<Quote[]>(API_URL.INSURANCE_QUOTES, { signal })).data,
    });
    return (
        <PageWithLoader isLoading={program.isPending || quotes.isPending} error={program.isError || quotes.isError}>
            <div className='page__header'>
                <div>
                    <Typography.Title level={1}>Event insurance</Typography.Title>
                    <Typography.Text>Prepare a quote application for your US running events.</Typography.Text>
                </div>
            </div>
            <div style={{ marginTop: 24 }}>
                {program.data && quotes.data && <QuoteWizard program={program.data} quotes={quotes.data} />}
            </div>
        </PageWithLoader>
    );
};

export default EventInsurance;
